import * as React from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import Tooltip from '../../Tooltip';
import useAriaDescribedByField from '../../../hooks/useAriaDescribedByField';
const GenericFormElement = props => {
  const {
    name,
    label,
    description,
    tooltip,
    children,
    meta,
    helpers,
    fieldType,
    defaultValue,
    className,
    containerClassName,
    verticalSpacing = true,
    visualValidation = true,
    optionalLabel,
    onTooltipOpen,
    onTooltipClose,
    isLabelHidden,
    expanded,
    maxCharacters,
    submitCount,
    isLiveValidationDisabled,
    hideValidationMessage
  } = props;
  const {
    t
  } = useTranslation();
  const errorMessagePrefix = "".concat(t('form.error.message_prefix'), ":");

  // Set a default value
  React.useEffect(() => {
    helpers.setTouched(false);
    if (defaultValue) {
      helpers.setValue(defaultValue || '');
    }
  }, [defaultValue]);
  const validationState = React.useMemo(() => {
    if (isLiveValidationDisabled && submitCount === 0) {
      return 'pending';
    }
    if (isLiveValidationDisabled && submitCount > 0 && meta.error) {
      return 'error';
    }
    if (isLiveValidationDisabled && submitCount > 0 && !meta.error) {
      return 'valid';
    }
    if (meta.touched && meta.error) {
      return 'error';
    }
    if (meta.touched && !meta.error) {
      return 'valid';
    }
    return 'pending';
  }, [meta.error, meta.touched, submitCount, isLiveValidationDisabled]);
  const isCharacterCountExceeded = maxCharacters && String(meta.value).length > maxCharacters;

  // Set some input container classes
  const inputWrapperClassNames = c(containerClassName, {
    'input-container': fieldType !== 'radioGroup',
    'input-container--select': fieldType === 'select',
    'input-container--toggle': fieldType === 'toggle',
    'input-container--filterCheckbox': fieldType === 'filterCheckbox',
    'input-container--expanded': expanded,
    'input-container--character-count-exceeded': isCharacterCountExceeded,
    error: visualValidation && validationState === 'error',
    valid: visualValidation && validationState === 'valid'
  });
  const classes = c(className, {
    'form-item': verticalSpacing
  });
  const ContainerElement = fieldType === 'radioGroup' ? 'fieldset' : 'div';
  const LabelElement = fieldType === 'radioGroup' ? 'legend' : 'label';
  const {
    ariaDescribedBy
  } = useAriaDescribedByField({
    name,
    meta,
    description: !!description
  });

  // Render the form item
  return /*#__PURE__*/React.createElement(ContainerElement, {
    className: classes,
    "aria-describedby": LabelElement === 'legend' ? ariaDescribedBy : ''
  }, label && fieldType !== 'checkbox' && fieldType !== 'toggle' && fieldType !== 'filterCheckbox' && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(LabelElement, {
    htmlFor: name,
    className: isLabelHidden ? 'sr-only' : ''
  }, label, optionalLabel && /*#__PURE__*/React.createElement("span", {
    className: "text-muted"
  }, " ", t('form.optional'))), tooltip && /*#__PURE__*/React.createElement(Tooltip, {
    title: tooltip,
    onOpen: onTooltipOpen,
    onClose: onTooltipClose
  })), description && /*#__PURE__*/React.createElement("span", {
    id: "".concat(name, "-description"),
    className: "description d-block"
  }, description), /*#__PURE__*/React.createElement("div", {
    className: inputWrapperClassNames
  }, children, label && (fieldType === 'checkbox' || fieldType === 'toggle' || fieldType === 'filterCheckbox') && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("label", {
    htmlFor: name
  }, label), tooltip && /*#__PURE__*/React.createElement(Tooltip, {
    title: tooltip,
    onOpen: onTooltipOpen,
    onClose: onTooltipClose
  }))), maxCharacters && !hideValidationMessage && /*#__PURE__*/React.createElement("div", {
    className: "d-flex mt-3 max-characters-indicator"
  }, validationState === 'error' ? /*#__PURE__*/React.createElement("span", {
    "aria-live": "polite",
    id: "".concat(name, "-error"),
    className: "form-error"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, errorMessagePrefix), meta.error) : /*#__PURE__*/React.createElement("p", {
    className: "text-small mb-0"
  }, t('form.textarea.maxChars', {
    maxCharacters
  })), /*#__PURE__*/React.createElement("p", {
    className: "ml-auto text-small mb-0"
  }, meta.value ? maxCharacters - String(meta.value).length : maxCharacters)), validationState === 'error' && !maxCharacters && !hideValidationMessage ? /*#__PURE__*/React.createElement("span", {
    "aria-live": "polite",
    id: "".concat(name, "-error"),
    className: "form-error"
  }, /*#__PURE__*/React.createElement("span", {
    className: "sr-only"
  }, errorMessagePrefix), meta.error) : null);
};
export default GenericFormElement;